import { CustomerType, DiscountType, OrderType } from '@repo/common';
import { InvoiceGenerator } from '@repo/pdf';
import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { IwFormRef } from '@components/form';

import { useDebounce } from '@hooks/use-debounce';

import { Route } from '@/constants';
import {
    useAddToCartMutation,
    useConfirmOrderMultiPaymentMutation,
    useGetActiveCartQuery,
    useLazyClearCartQuery,
    useRemoveSingleCartItemMutation,
    useUpdateCartByIdMutation,
    useUpdateCartMutation,
} from '@/features/slice/cart';
import { useGetStoreSettingsQuery } from '@/features/slice/settings';
import { toastError } from '@/utils';
import { mapOrderToInvoice } from '@/utils/mappers';

import { INITIAL_VALUE, TAddPaymentForm } from './add-payment-form.schema';

export const useCart = () => {
    const navigate = useNavigate();
    const [showDiscount, setShowDiscount] = useState(true);

    const formRef = useRef<IwFormRef<TAddPaymentForm>>(null);

    // Get the active cart
    const {
        data,
        isLoading: isActiveCartLoading,
        isError: isActiveCartError,
        isFetching: isActiveCartFetching,
    } = useGetActiveCartQuery('');

    // Add item from product selector to cart
    const [addItemToCart, { isLoading: isAddItemToCartLoading }] =
        useAddToCartMutation();

    // Confirm order with payment information
    const [
        confirmOrderMultiPayment,
        { isLoading: isConfirmOrderMultiPaymentLoading },
    ] = useConfirmOrderMultiPaymentMutation();

    const [
        clearCart,
        {
            isLoading: isClearCartLoading,
            error: clearCartError,
            isError: isClearCartError,
        },
    ] = useLazyClearCartQuery();

    // Remove item from cart
    const [
        removeCartItem,
        {
            isLoading: isRemoveItemLoading,
            isError: isRemoveItemError,
            error: removeItemError,
        },
    ] = useRemoveSingleCartItemMutation();

    // Update cart type, customer, discount
    const [
        updateCartInfo,
        {
            isLoading: isUpdateCartInfoLoading,
            isError: isUpdateCartInfoError,
            error: updateCartInfoError,
        },
    ] = useUpdateCartMutation();

    // Store settings for generating invoice
    const { data: storeSettings } = useGetStoreSettingsQuery('');

    const [updateCartItemById, { isLoading: isUpdateCartItemByIdLoading }] =
        useUpdateCartByIdMutation();

    const resetPaymentForm = () => {
        setTimeout(() => {
            setTimeout(() => {
                formRef.current?.reset(INITIAL_VALUE);
                // @ts-expect-error wrong ts type
                formRef.current?.setValue('payments.0.amount', '');
            }, 0);
        });
    };

    const handleConfirmOrderMultiPayment = async (
        orderData: {
            payments: {
                amount: number;
                transaction_id: string;
                payment_method: string;
            }[];
        },
        shouldPrint: boolean = false,
    ) => {
        try {
            const response = await confirmOrderMultiPayment(orderData).unwrap();
            resetPaymentForm();

            toast.success('Order successfully', {
                action: {
                    label: 'View Order',
                    onClick: () => {
                        navigate(`${Route.SALES}/${response?.data?.id}`);
                    },
                },
            });

            if (shouldPrint) {
                const invoice = new InvoiceGenerator(
                    mapOrderToInvoice(
                        response.data,
                        storeSettings?.data as any,
                    ),
                    {
                        type: 'sale',
                        showDiscount,
                    },
                );

                invoice.print();
            }
        } catch (error) {
            toastError(error, 'useCart:handleConfirmOrderMultiPayment');
        }
    };

    const changeOrderType = async (orderType: OrderType) => {
        try {
            await updateCartInfo({
                customer_type: orderType,
                customer_id: null,
                discount_value: 0,
                discount_type: DiscountType.Fixed,
                discount_id: null,
                tax: 0,
                note: '',
            }).unwrap();
        } catch (error) {
            toastError(error, 'useCart:changeOrderType');
        }
    };

    const updateCustomer = async (customerId: string | null) => {
        try {
            await updateCartInfo({
                customer_type:
                    data?.data?.info?.customer_type ?? OrderType.RETAIL,
                customer_id: customerId,
                discount_value: data?.data?.info?.discount_value ?? 0,
                discount_type:
                    data?.data?.info?.discount_type ?? DiscountType.Fixed,
                discount_id: null,
                tax: data?.data?.info?.tax ?? 0,
                note: data?.data?.info?.note ?? 0,
            }).unwrap();
        } catch (error) {
            toastError(error, 'useCart:updateCustomer');
        }
    };

    const updateDiscount = async (
        discountType: DiscountType,
        discountValue: number,
    ) => {
        // const isDiscountValueNaN = Number.isNaN(discountValue);
        //
        // if (isDiscountValueNaN) {
        //     return;
        // }

        const hasValueChanged =
            data?.data?.info?.discount_value !== discountValue;

        const hasTypeChanged = data?.data?.info?.discount_type !== discountType;

        if (!hasValueChanged && !hasTypeChanged) {
            return;
        }

        try {
            await updateCartInfo({
                customer_type:
                    data?.data?.info?.customer_type ?? OrderType.RETAIL,
                customer_id: data?.data?.info?.customer_id,
                discount_value: discountValue ?? 0,
                discount_type: discountType,
                discount_id: null,
                tax: data?.data?.info?.tax ?? 0,
                note: data?.data?.info?.note ?? 0,
            }).unwrap();
        } catch (error) {
            toastError(error, 'useCart:updateDiscount');
        }
    };

    const handleSubmitCart = async (data: TAddPaymentForm) => {
        handleConfirmOrderMultiPayment(
            {
                payments: data?.payments?.map((payment) => ({
                    amount: payment.amount ?? 0,
                    payment_method: payment.payment_method,
                    transaction_id: payment.transaction_id,
                })),
            },
            false,
        );
    };
    const handleSubmitCartWithPrint = async (data: TAddPaymentForm) => {
        await handleConfirmOrderMultiPayment(
            {
                payments: data?.payments?.map((payment) => ({
                    amount: payment.amount ?? 0,
                    payment_method: payment.payment_method,
                    transaction_id: payment.transaction_id,
                })),
            },
            true,
        );
    };

    const handleUpdateCartItem = useDebounce(
        async (
            cartItemId: string,
            updates: {
                quantity: number;
                discount_value: number;
                discount_type: DiscountType;
                price: number;
            },
        ) => {
            try {
                await updateCartItemById({
                    id: cartItemId,
                    ...updates,
                }).unwrap();
                console.log('Update cart item');
            } catch (error) {
                toastError(error, 'useCart:handleUpdateCartItem');
            }
        },
        500,
    );

    let orderType = data?.data?.info?.customer_type as CustomerType;
    if (!orderType) {
        orderType = CustomerType.RETAIL;
    }
    if (orderType === CustomerType.WALKIN) {
        orderType = CustomerType.RETAIL;
    }

    return {
        // Get the active cart
        activeCart: data,
        isActiveCartLoading,
        isActiveCartError,
        isActiveCartFetching,
        orderType,

        // Add item
        addItemToCart,
        isAddItemToCartLoading,

        // Order Confirmation
        confirmOrderMultiPayment,
        isConfirmOrderMultiPaymentLoading,
        handleConfirmOrderMultiPayment,
        handleSubmitCart,
        handleSubmitCartWithPrint,

        // Remove item
        removeCartItem,
        isRemoveItemError,
        isRemoveItemLoading,
        removeItemError,

        // Update Cart
        changeOrderType,
        updateCustomer,
        updateDiscount,
        isUpdateCartInfoError,
        isUpdateCartInfoLoading,
        updateCartInfoError,

        // Clear cart
        clearCart,
        isClearCartError,
        isClearCartLoading,
        clearCartError,

        formRef,

        showDiscount,
        setShowDiscount,

        handleUpdateCartItem,
        isUpdateCartItemByIdLoading,
    };
};
